// Pieces of the space policy form that belong to the data-loss-prevention rules (GEDX-FE-03):
// the "Liens de partage autorisés" switch, the note shown when the plan has no `feature.dlp`, and
// the upgrade message for a 403 PLAN_LIMIT_REACHED. Tightening a rule needs `feature.dlp` (Business
// and Enterprise); loosening one never does.
import { useCan, useEntitlement } from '@batiflow/client-core/react';
import { UpgradePrompt } from '@batiflow/ui';
import { MessageBar, MessageBarBody, Switch, __css, tokens } from '@fluentui/react-components';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

const useStyles = __css({"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"}});

/** Whether the plan includes the data-loss-prevention rules. */
export const useDlpEntitled = (): boolean => useEntitlement('feature.dlp').enabled;

/**
 * Whether a rule's switch is locked: without `feature.dlp`, a rule that is not tightened yet
 * cannot be (a rule already tightened can still be loosened, and put back before saving).
 */
export const tighteningLocked = (entitled: boolean, savedIsPermissive: boolean): boolean =>
  !entitled && savedIsPermissive;

/** Said above the rules when the plan cannot tighten them. */
export function DlpLockedNotice() {
  const { t } = useTranslation('shareLinks');
  return (
    <MessageBar intent="info" layout="multiline">
      <MessageBarBody>{t('policy.locked')}</MessageBarBody>
    </MessageBar>
  );
}

export interface ShareLinksSwitchProps {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

/** "Liens de partage autorisés" with its one-sentence explanation. */
export function ShareLinksSwitch({ checked, disabled, onChange }: ShareLinksSwitchProps) {
  const { t } = useTranslation('shareLinks');
  const styles = useStyles();
  return (
    <>
      <Switch
        label={t('policy.links.label')}
        checked={checked}
        disabled={disabled}
        onChange={(_event, data) => {
          onChange(data.checked);
        }}
      />
      <p className={styles.note}>{t('policy.links.hint')}</p>
    </>
  );
}

/**
 * The plan lacks the feature (403 PLAN_LIMIT_REACHED on `feature.dlp`): the owner, who may change
 * the plan, gets a link to the offers; everyone else is told to ask their administrator.
 */
export function DlpUpgradePrompt({ orgId }: { orgId: string }) {
  const { t } = useTranslation('shareLinks');
  const navigate = useNavigate();
  const owner = useCan('billing.manage');
  return (
    <UpgradePrompt
      title={t('policy.upgrade.title')}
      message={t(owner ? 'policy.upgrade.bodyOwner' : 'policy.upgrade.bodyMember')}
      {...(owner
        ? {
            onUpgrade: () => {
              void navigate({ to: '/o/$orgId/admin/subscription', params: { orgId } });
            },
          }
        : {})}
    />
  );
}


import "/opt/batiflow/app/apps/web/src/features/share-links/policy-fields.griffel.css";